import React, { Fragment } from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useNavigation, useRoute } from '@react-navigation/native';

import type { Section } from '@models/Sections/SectionModel';
import {
  HorizontalTabView,
  HorizontalList,
  TutorialItem,
  Separator,
  CourseItem,
} from '@components';
import { useAsyncInit, useHorizontalTabView } from '@hooks';
import { AnalyticsService, ToastService } from '@services';
import { parseApiError } from '@utils';
import {
  TutorialListTypeEnum,
  TUTORIAL_EXTRA_CATEGORY_ID_BY_LIST_TYPE,
  CourseListTypeEnum,
} from '@constants';
import {
  HomeTabNavigatorNavigationPropType,
  HomeTabNavigatorRoutePropType,
  screens,
} from '@navigation';
import type { Tutorial } from '@models/Tutorials/TutorialModel';
import type { Course } from '@models/Courses/CourseModel';
import { useSectionsModel } from '@models/Sections/SectionsModel';

const Container = styled(View)({
  flex: 1,
  backgroundColor: '$background0',
});

type NavigationPropType =
  HomeTabNavigatorNavigationPropType<'Latest'>;

type RoutePropType = HomeTabNavigatorRoutePropType<'Latest'>;

export const LatestScreen = observer(() => {
  const nav = useNavigation<NavigationPropType>();
  const route = useRoute<RoutePropType>();
  const { t } = useTranslation();
  const sections = useSectionsModel();

  const { tutorialItemWidth } = useHorizontalTabView();

  useAsyncInit(() => fetch());

  async function fetch() {
    try {
      await sections.latest.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function renderTitleByType(section: Section) {
    if (section.isCourseSection) {
      switch (section.coursesListType) {
        case CourseListTypeEnum.Recommended:
          return {
            text: t('home.courses', {
              context: section.coursesListType,
            }),
            badge: t('home.new'),
          };
        default:
          return t('home.courses', {
            context: section.coursesListType,
          });
      }
    }
    switch (section.videosListType) {
      case TutorialListTypeEnum.Tag:
        return `#${section.name}`;
      default:
        return t('home.tutorials', {
          context: section.videosListType,
        });
    }
  }

  function navigateToSeeMoreBySectionType(section: Section) {
    if (section.isCourseSection) {
      return nav.push(screens.HubNavigator, {
        screen: screens.Courses,
        params: {
          type: section.coursesListType,
        },
      });
    }

    switch (section.videosListType) {
      case TutorialListTypeEnum.Tag:
        return nav.push(screens.HubNavigator, {
          screen: screens.Tutorials,
          params: {
            type: section.videosListType,
            id: section.params?.videoTagId as number,
            name: section.name,
          },
        });
      default:
        return nav.push(screens.HubNavigator, {
          screen: screens.Tutorials,
          params: {
            type: section.videosListType,
          },
        });
    }
  }

  function navigateToTutorial(item: Tutorial, section: Section) {
    // using there 'push' to prevent cashing tutorial screen
    nav.push(screens.HubNavigator, {
      screen: screens.Tutorial,
      params: {
        id: item.id,
        extraCategoryId:
          TUTORIAL_EXTRA_CATEGORY_ID_BY_LIST_TYPE[section.listType],
      },
    });

    AnalyticsService.trackHomeContent({
      contentTitle: item.title,
    });
  }

  function navigateToCourse(course: Course) {
    // using there 'push' to prevent cashing course screen
    nav.push(screens.HubNavigator, {
      screen: screens.CourseSummary,
      params: {
        id: course.id,
      },
    });

    AnalyticsService.trackHomeContent({
      contentTitle: course.title,
      courseId: course.id,
    });
  }

  function renderHorizontalListItemByType(
    section: Section,
    item: Tutorial | Course,
  ) {
    if (section.isCourseSection) {
      return (
        <CourseItem
          width={tutorialItemWidth}
          key={item.id}
          item={item as Course}
          onPress={navigateToCourse}
        />
      );
    }

    return (
      <TutorialItem
        width={tutorialItemWidth}
        key={item.id}
        item={item as Tutorial}
        onPress={() => navigateToTutorial(item as Tutorial, section)}
      />
    );
  }

  const isLoading =
    sections.latest.fetch.inProgress &&
    !sections.latest.fetch.hasEverBeenRun;

  const isRefreshing = sections.latest.fetch.inProgress;

  // sections cannot be empty
  const isError = sections.latest.isEmpty;

  return (
    <Container>
      <HorizontalTabView
        routeName={route.name}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        isError={isError}
        onRefresh={fetch}
      >
        <Separator size="$9" />
        {sections.latest.asArray.map(
          (latestSection, index, latestSections) => {
            const isNotLastHorizontalList =
              latestSections.length - 1 !== index;

            if (latestSection.isEmpty) {
              return;
            }

            return (
              <Fragment key={latestSection.name + index}>
                <HorizontalList<Course | Tutorial>
                  title={renderTitleByType(latestSection)}
                  data={latestSection.preview}
                  onMorePress={() => {
                    navigateToSeeMoreBySectionType(latestSection);
                  }}
                  renderItem={({ item }) =>
                    renderHorizontalListItemByType(
                      latestSection,
                      item,
                    )
                  }
                  keyExtractor={(i) => i.id.toString()}
                  ItemSeparatorComponent={() => (
                    <Separator size={['$6', '$8']} vertical />
                  )}
                />

                {isNotLastHorizontalList && <Separator size="$9" />}
              </Fragment>
            );
          },
        )}
        <Separator />
      </HorizontalTabView>
    </Container>
  );
});
